import { createRouter, createWebHistory } from "vue-router";

import { logoutUser } from "@/components/service/api";
import {
  useAiPhotoStore,
  useAppStore,
  useMasterCreateStore,
  useMaterialLibraryStore,
  usePermissionsStore,
  useProductsStore,
  useRenderStore,
  useWidgetStore,
} from "@/store/modules";

import pinia from "../store";

const routes = [
  {
    path: "/hotspot",
    name: "hotspot",
    component: () => import("@/components/Widget/EditWidgetComponents/Hotspots/CreateHotspotVue.vue"),
  },
  {
    path: "/",
    name: "home",
    component: () => import("@/components/Product/ProductsVue.vue"),
  },
  {
    path: "/tags",
    redirect: { path: "/", query: { tab: "tags" } },
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/components/Login/LoginVue.vue"),
  },
  {
    path: "/tariff/:id",
    name: "tariff",
    component: () => import("@/components/Tariff/TariffVue.vue"),
  },
  {
    path: "/profile",
    name: "profile",
    component: () => import("@/components/User/UserVue.vue"),
  },
  {
    path: "/teams",
    name: "teams",
    component: () => import("@/components/Teams/MyTeamsVue.vue"),
  },
  {
    path: "/product",
    name: "product",
    component: () => import("@/components/Product/ProductsVue.vue"),
    children: [
      {
        path: "new",
        name: "product-create",
        component: () => import("@/components/CreateMaster/CreateMasterVue.vue"),
      },
      {
        path: ":id",
        name: "product-edit",
        component: () => import("@/components/CreateMaster/CreateMasterVue.vue"),
      },
    ],
  },
  {
    path: "/render",
    name: "render",
    component: () => import("@/components/Render/RenderPageVue.vue"),
    children: [
      {
        path: "new",
        name: "create-render",
        component: () => import("@/components/Render/CRUD/RenderPageNewVue.vue"),
      },
      {
        path: ":id",
        name: "create-render-edit",
        component: () => import("@/components/Render/CRUD/RenderPageNewVue.vue"),
      },
      // {
      //   path: "new",
      //   name: "create-render",
      //   component: () => import("@/EditMaterialComponents/Render/CRUD/CreateRenderVue.vue"),
      // },
      // {
      //   path: ":id",
      //   name: "create-render-edit",
      //   component: () => import("@/EditMaterialComponents/Render/CRUD/CreateRenderVue.vue"),
      // },
    ],
  },
  {
    path: "/ai",
    name: "ai",
    component: () => import("@/components/AiPhoto/Page/AiPhotoPageVue.vue"),
  },
  {
    path: "/ai/photo",
    name: "ai-photo",
    component: () => import("@/components/AiPhoto/Page/AiPhotoPageVue.vue"),
    children: [
      {
        path: "new",
        name: "ai-photo-new",
        component: () => import("@/components/AiPhoto/Stepper/StepperVue.vue"),
      },
      {
        path: ":id",
        name: "ai-photo-edit",
        component: () => import("@/components/AiPhoto/Stepper/StepperVue.vue"),
      },
    ],
  },
  {
    path: "/ai/video",
    name: "ai-video",
    component: () => import("@/components/AiPhoto/Page/AiPhotoPageVue.vue"),
    children: [
      {
        path: "new",
        name: "ai-video-new",
        component: () => import("@/components/AiPhoto/Stepper/StepperVue.vue"),
      },
      {
        path: ":id",
        name: "ai-video-edit",
        component: () => import("@/components/AiPhoto/Stepper/StepperVue.vue"),
      },
    ],
  },
  {
    path: "/material-library",
    name: "material-library",
    component: () => import("@/components/MaterialLibrary/MaterialLibraryPage.vue"),
  },
  {
    path: "/oauth/:provider",
    name: "oauth",
    props: (route) => ({ params: route.query }),
    component: () => import("@/components/Utils/OauthProviderVue.vue"),
  },
  {
    path: "/loginForDeveloper",
    name: "loginForDeveloper",
    props: (route) => ({ params: route.query }),
    component: () => import("@/components/Login/LoginForDeveloper.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

const aiPhotoStore = useAiPhotoStore(pinia);
const appStore = useAppStore(pinia);
const masterCreateStore = useMasterCreateStore(pinia);
const materialLibraryStore = useMaterialLibraryStore(pinia);
const permissionsStore = usePermissionsStore(pinia);
const productsStore = useProductsStore(pinia);
const renderStore = useRenderStore(pinia);
const widgetStore = useWidgetStore(pinia);

router.beforeEach(async (to, from, next) => {
  if (to.path === "/" && localStorage.getItem("returnToProfile")) {
    localStorage.removeItem("returnToProfile");
    return next("/profile");
  }
  if (from.path === "oauth" && localStorage.getItem("fromOauthLazyTab")) {
    location.reload(true);
  }
  if (import.meta.env.DEV && to.path === "/login") {
    return next("/loginForDeveloper");
  }
  if (to.path === "/login") {
    await logoutUser();
    return next(false);
  }
  if (to.path === "/render/new" || to.path === "/render" || to.path.startsWith("/render/")) {
    masterCreateStore.SET_VALUE({ key: "mode", value: "render" });
  } else if (
    to.path === "/ai" ||
    to.path === "/ai/photo" ||
    to.path.startsWith("/ai/photo/") ||
    to.path === "/ai/video" ||
    to.path.startsWith("/ai/video/")
  ) {
    masterCreateStore.SET_VALUE({ key: "mode", value: "ai-photo" });
  } else if (to.path === "/material-library" || to.path.startsWith("/material-library/")) {
    masterCreateStore.SET_VALUE({ key: "mode", value: "library" });
  } else {
    masterCreateStore.SET_VALUE({ key: "mode", value: null });
  }

  if (from.path === "/render/new" || from.path.startsWith("/render/")) {
    productsStore.SET_VALUE({ key: "product", value: null });
    productsStore.SET_VALUE({ key: "productId", value: null });
    renderStore.SET_VALUE({ key: "renderStep", value: 1 });
    renderStore.SET_VALUE({ key: "selectedModelForRender", value: null });
    renderStore.SET_VALUE({ key: "render", value: null });
  }

  if (from.path === "/material-library") {
    materialLibraryStore.SET_VALUE({
      key: "isEditFolder",
      value: false,
    });
  }

  if (from.path.startsWith("/ai/photo/") || from.path.startsWith("/ai/video/")) {
    productsStore.SET_VALUE({ key: "product", value: null });
    productsStore.SET_VALUE({ key: "productId", value: null });
    aiPhotoStore.SET_VALUE({ key: "selectedPhoto", value: null });
    aiPhotoStore.SET_VALUE({ key: "AiPhotoStep", value: 0 });
    widgetStore.SET_VALUE({ key: "currentWidget", value: {} });
  }

  const patternRender = /^\/render(\/|$)/;

  if (patternRender.test(to.path) && permissionsStore.scopesAndPlans.length !== 0) {
    let render = permissionsStore.scopesAndPlans.billing.find((scope) => scope.code === "render360");

    if (render) {
      appStore.SET_VALUE({ key: "lastLocation", value: from.path });
      next();
    } else {
      next(false);
    }
  } else {
    appStore.SET_VALUE({ key: "lastLocation", value: from.path });
    next();
  }
});

export default router;
